/**
 * API client.
 *
 * Auth is one header. Inside Telegram it carries the signed `initData`; in a
 * plain browser it falls back to `X-Dev-User-Id`, which the server accepts only
 * while ENV=dev. That fallback is what makes the app developable without a bot.
 */

import { webApp } from "./telegram";

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

function authHeaders(): Record<string, string> {
  const app = webApp();
  if (app?.initData) return { Authorization: `tma ${app.initData}` };
  const dev = new URLSearchParams(location.search).get("dev_user");
  return dev ? { "X-Dev-User-Id": dev } : {};
}

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
}

async function get<T>(path: string, params?: Record<string, string | number | undefined>) {
  const url = new URL(`${BASE}${path}`, location.origin);
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
  }
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(res.status, detail.detail ?? res.statusText);
  }
  return (await res.json()) as T;
}

// ------------------------------------------------------------------- types --

export interface Health {
  counts: Record<string, number | string | null>;
  phases: Record<string, boolean>;
}

export interface Stats {
  msg_count: number;
  threads_started: number;
  reactions_received: number;
  replies_received: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface Evidence {
  tg_msg_id: number;
  text: string;
  ts: string;
  thread_root: number;
}

export interface Expertise {
  topic_slug: string;
  topic_name: string;
  score: number;
  evidence: Evidence[];
}

export interface ThreadCard {
  root_msg_id: number;
  started_at: string;
  msg_count: number;
  participant_count: number;
  reaction_total?: number;
  title: string | null;
  summary: string | null;
  outcome?: string | null;
  quality?: number | null;
  author?: string | null;
  author_id?: number | null;
}

export interface Card {
  known: boolean;
  is_self?: boolean;
  message?: string;
  member: { tg_user_id: number; display_name: string | null; username: string | null };
  stats: Stats;
  profile: {
    headline: string | null;
    role: string | null;
    org: string | null;
    bio: string | null;
    asks_about: string[] | null;
    answers_about: string[] | null;
  } | null;
  expertise: Expertise[];
  activity: Record<string, number>;
  threads: ThreadCard[];
  links: { kind: string; value: string }[];
  channels: { handle: string | null; title: string | null; subscribers: number | null }[];
}

export interface PersonRow {
  tg_user_id: number;
  display_name: string | null;
  username: string | null;
  msg_count: number;
  threads_started: number;
  reactions_received: number;
  last_seen: string | null;
  headline: string | null;
  role: string | null;
  org: string | null;
  topic_slugs: string[];
}

export interface FeedItem {
  kind: "thread" | "post";
  id: number;
  ts: string;
  title: string | null;
  summary: string | null;
  author: string | null;
  author_id: number | null;
  channel: string | null;
  channel_handle: string | null;
  msg_count: number;
  participant_count: number;
  reaction_total: number;
  /** 1-5 substance score from the enrichment pass; 2 stands in before it runs. */
  quality: number;
  score: number;
}

export interface Topic {
  slug: string;
  name: string;
  description: string | null;
  thread_count: number;
  total_threads: number;
  children: Topic[];
}

export interface ThreadDetail {
  thread: ThreadCard & { question?: string | null; tools?: string[] | null };
  messages: {
    tg_msg_id: number;
    ts: string;
    text: string;
    author: string | null;
    author_id: number | null;
    reaction_count: number;
    is_channel: boolean;
  }[];
  topics: { slug: string; name: string }[];
}

export interface SearchResult {
  query: string;
  mode: "hybrid" | "full-text";
  threads: ThreadCard[];
  people: PersonRow[];
  posts: { id: number; ts: string; text: string; channel: string; handle: string }[];
}

// ----------------------------------------------------------------- surface --

export const api = {
  health: () => get<Health>("/health"),
  me: () => get<Card>("/me"),
  person: (id: number) => get<Card>(`/people/${id}`),
  people: (p: { q?: string; topic?: string; sort?: string; limit?: number; offset?: number }) =>
    get<PersonRow[]>("/people", p),
  feed: (p: { topic?: string; kind?: string; limit?: number; offset?: number }) =>
    get<FeedItem[]>("/feed", p),
  topics: () => get<Topic[]>("/topics"),
  topic: (slug: string) => get<{ topic: Topic; threads: ThreadCard[] }>(`/topics/${slug}`),
  thread: (id: number) => get<ThreadDetail>(`/threads/${id}`),
  search: (q: string) => get<SearchResult>("/search", { q }),
};
